// =============================================================
// 📁 src/pages/planificateur/PointsEdit.tsx
// 💎 Platinum PRO 2025 — Édition des points QR (ALIGNÉ BACKEND)
// ✅ GET /descentes/:id (include points)
// ✅ PUT /descentes/:id { points: [...] } (replace complet)
// =============================================================

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { api } from "../../../lib/axios";
import { useAuth } from "../../../context/AuthContext";
import { Html5QrcodeScanner } from "html5-qrcode";


/* =============================================================
   🧩 Types alignés backend
============================================================= */
type SecteurPoint =
  | "MENAGE"
  | "PME"
  | "INDUSTRIE"
  | "ONG"
  | "INSTITUTION"
  | "HOPITAL"
  | "ECOLE"
  | "UNIVERSITE"
  | "EGLISE"
  | "MINISTERE"
  | "MARCHE";

type TypeContratClient =
  | "MENAGE"
  | "PROFESSIONNEL"
  | "CHANTIER";

type SecteurClient =
  | "PME"
  | "INDUSTRIE"
  | "ONG"
  | "INSTITUTION"
  | "HOPITAL"
  | "ECOLE"
  | "UNIVERSITE"
  | "EGLISE"
  | "MINISTERE"
  | "MARCHE";

interface Point {
  id?: number;
  ordre: number;
  clientId?: number | null;
  nom?: string | null;
  adresse?: string | null;   // ✅ AJOUT ICI
  qr: string;
  secteur: SecteurPoint;
  commentaire?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface ClientLite {
  id: number;
  nom?: string | null;
  prenom?: string | null;
  raisonSociale?: string | null;
  type: TypeContratClient;
  secteur?: SecteurClient | null;
}

/* =============================================================
   📌 Labels UI (optionnel)
============================================================= */
const secteurLabel: Record<SecteurPoint, string> = {
  MENAGE: "Ménage",
  PME: "PME",
  INDUSTRIE: "Industrie",
  ONG: "ONG",
  INSTITUTION: "Institution",
  HOPITAL: "Hôpital",
  ECOLE: "École",
  UNIVERSITE: "Université",
  EGLISE: "Église",
  MINISTERE: "Ministère",
  MARCHE: "Marché",
};

/* =============================================================
   ⏳ Loader
============================================================= */
const Loader: React.FC = () => (
  <div className="animate-pulse text-center text-gray-400">
    <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto animate-spin mb-3" />
    Chargement en cours...
  </div>
);

const toNumberSafe = (v: any): number | null => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normalizeSecteur = (v: any): SecteurPoint =>
  (Object.keys(secteurLabel) as SecteurPoint[]).includes(v)
    ? (v as SecteurPoint)
    : "MENAGE";

/* =============================================================
   🎯 Composant principal
============================================================= */
const PointsEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const descenteId = Number(id || 0);
  const navigate = useNavigate();
  const { token } = useAuth() as any;

  const [points, setPoints] = useState<Point[]>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [clientResults, setClientResults] = useState<Record<number, ClientLite[]>>({});

  // options select
  const secteurOptions = useMemo(
    () => Object.keys(secteurLabel) as SecteurPoint[],
    []
  );

/* =============================================================
   🚀 Chargement initial (backend)
============================================================= */
useEffect(() => {
  const fetchPoints = async () => {
    if (!descenteId) return;

    setLoading(true);
    try {
      const res = await api.get(`/descentes/${descenteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = res.data?.data;
      const pts = Array.isArray(d?.points) ? d.points : [];

      const sorted: Point[] = pts
        .slice()
        .sort((a: any, b: any) => (a.ordre ?? 0) - (b.ordre ?? 0))
        .map((p: any, idx: number) => ({
          id: p.id,
          ordre: p.ordre ?? idx + 1,
          secteur: normalizeSecteur(p.secteur),
          qr: p.qr ?? "-",
          nom: p.nom ?? null,
          adresse: p.adresse ?? null, // ✅ AJOUT ICI
          commentaire: p.commentaire ?? null,
          latitude: p.latitude ?? null,
          longitude: p.longitude ?? null,
        }));

      setPoints(sorted);
    } catch (err: any) {
      console.error(err);
      toast.error(
        "❌ Erreur chargement points : " +
          (err?.response?.data?.error || err?.message || "Erreur inconnue")
      );
    } finally {
      setLoading(false);
    }
  };
 
  fetchPoints();
}, [descenteId, token]);

useEffect(() => {
  return () => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
  };
}, []);

  /* =============================================================
     ✏️ Modifs
  ============================================================= */
  const handleChange = (
    index: number,
    field: keyof Point,
    value: string | number
  ) => {
    const updated = [...points];
    updated[index] = { ...updated[index], [field]: value as any };
    setPoints(updated);
  };

  const handleAdd = () => {
    const nextOrdre = points.length
      ? Math.max(...points.map((p) => p.ordre || 0)) + 1
      : 1;

    setPoints([
      ...points,
      {
        ordre: nextOrdre,
        secteur: "MENAGE",
        qr: "",
        nom: null,
        commentaire: null,
        latitude: null,
        longitude: null,
      },
    ]);
  };

  const handleDelete = async (index: number) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce point ?")) return;

    const updated = [...points];
    updated.splice(index, 1);

    // réindex ordre automatiquement (propre)
    const reindexed = updated.map((p, i) => ({ ...p, ordre: i + 1 }));
    setPoints(reindexed);

    toast.info("🗑 Point supprimé (pense à Enregistrer)");
  };

const QRScanner = ({ onScan }: { onScan: (text: string) => void }) => {
  useEffect(() => {
    const scanner = new Html5QrcodeScanner(
      "reader",
      { fps: 10, qrbox: 250 },
      false
    );

    scanner.render(
      (decodedText) => {
        onScan(decodedText);
        scanner.clear();
      },
      (error) => {
        console.warn(error);
      }
    );

    return () => {
      scanner.clear().catch(() => {});
    };
  }, [onScan]);

  return <div id="reader" className="w-full" />;
};

const searchClient = (index: number, value: string) => {
  const query = value.trim();

  // 🔒 Annule timer précédent
  if (searchTimeout.current) {
    clearTimeout(searchTimeout.current);
  }

  // 🔒 Si moins de 2 caractères → reset
  if (query.length < 2) {
    setClientResults((prev) => ({
      ...prev,
      [index]: [],
    }));
    return;
  }

  // ⏳ Debounce 300ms
  searchTimeout.current = setTimeout(async () => {
    try {
      const res = await api.get("/clients/search", {
        params: { q: query, limit: 5 },
        headers: { Authorization: `Bearer ${token}` },
      });

      const results = Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      setClientResults((prev) => ({
        ...prev,
        [index]: results,
      }));
    } catch (error) {
      console.error("❌ Erreur recherche client:", error);

      setClientResults((prev) => ({
        ...prev,
        [index]: [],
      }));
    }
  }, 300);
};
 
/* =============================================================
   ✅ Validation (QR case-insensitive sécurisé)
============================================================= */
const validate = (): boolean => {
  const newErrors: Record<number, string> = {};
  const qrSet = new Set<string>();
  const ordreSet = new Set<number>();
  let valid = true;

  points.forEach((p, i) => {
    const qr = String(p.qr || "").trim().toUpperCase(); // ✅ NORMALISATION

    if (!qr) {
      newErrors[i] = "Code QR requis";
      valid = false;
    } else if (qrSet.has(qr)) {
      newErrors[i] = "QR déjà utilisé";
      valid = false;
    }

    const ordre = Number(p.ordre);

    if (!Number.isFinite(ordre) || ordre <= 0) {
      newErrors[i] = "Ordre invalide";
      valid = false;
    } else if (ordreSet.has(ordre)) {
      newErrors[i] = "Ordre en double";
      valid = false;
    }

    qrSet.add(qr);
    ordreSet.add(ordre);
  });

  setErrors(newErrors);

  if (!valid) {
    toast.warning("⚠️ Corrige les erreurs avant d’enregistrer");
  }

  return valid;
};

/* =============================================================
   💾 Sauvegarde (PUT /descentes/:id { points })
============================================================= */
const handleSave = async () => {
  if (!descenteId) return toast.error("❌ ID descente manquant.");
  if (points.length === 0)
    return toast.warning("⚠️ Aucun point à enregistrer.");

  if (!validate()) return;

  try {
    setSaving(true);

    const payloadPoints = points
      .slice()
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
      .map((p, idx) => ({
        ordre: p.ordre ?? idx + 1,
        secteur: normalizeSecteur(p.secteur),
        qr: String(p.qr).trim().toUpperCase(), // normalisation QR
        nom: p.nom ?? null,
        adresse: p.adresse ?? null,
        commentaire: p.commentaire ?? null,
        latitude: toNumberSafe(p.latitude),
        longitude: toNumberSafe(p.longitude),

        // 🔥 IMPORTANT POUR BACKEND
        clientId: p.clientId ?? null,
      }));

    await api.put(
      `/descentes/${descenteId}`,
      { points: payloadPoints },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("✅ Points enregistrés avec succès !");
  } catch (err: any) {
    console.error(err);

    toast.error(
      "❌ Erreur enregistrement : " +
        (err?.response?.data?.error ||
          err?.message ||
          "Erreur inconnue")
    );
  } finally {
    setSaving(false);
  }
};
 
  /* =============================================================
     🌀 Chargement
  ============================================================= */
  if (loading)
    return (
      <div className="min-h-screen flex justify-center items-center bg-slate-900 text-gray-300">
        <Loader />
      </div>
    );

  /* =============================================================
     🎨 Rendu
  ============================================================= */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-gray-100 p-6 flex justify-center">
      <ToastContainer position="top-right" />
      <motion.div
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-5xl bg-slate-800 rounded-xl shadow-2xl border border-slate-700 p-6 space-y-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            📍 Points QR — Descente #{descenteId}
            <span className="text-xs bg-blue-700 px-2 py-1 rounded">
              {points.length} points
            </span>
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="bg-slate-700 hover:bg-slate-600 text-gray-100 px-4 py-2 rounded-lg shadow-md transition"
          >
            ◀ Retour
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-slate-700 shadow">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-800 text-gray-300 uppercase text-xs border-b border-slate-700">
              <tr>
                <th className="p-3 text-left w-20">Ordre</th>
                <th className="p-3 text-left">Secteur</th>
                <th className="p-3 text-left">QR Code</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
<tbody>
  {points.length === 0 ? (
    <tr>
      <td colSpan={4} className="text-center p-4 text-gray-400 italic">
        Aucun point enregistré
      </td>
    </tr>
  ) : (
    points.map((p, i) => (
      <tr
        key={p.id ?? `${p.ordre}-${i}`}
        className={`border-t border-slate-700 transition ${
          errors[i] ? "bg-slate-800/70" : "hover:bg-slate-800"
        }`}
      >
        {/* Ordre */}
        <td className="p-3">
          <input
            type="number"
            value={p.ordre}
            onChange={(e) =>
              handleChange(i, "ordre", Number(e.target.value))
            }
            className={`w-full bg-slate-900 border ${
              errors[i] ? "border-red-500" : "border-slate-700"
            } text-gray-100 rounded px-2 py-1`}
          />
        </td>

        {/* Secteur (auto rempli si client sélectionné) */}
        <td className="p-3">
          <select
            value={p.secteur}
            onChange={(e) =>
              handleChange(i, "secteur", e.target.value)
            }
            className="bg-slate-900 border border-slate-700 text-gray-100 rounded px-2 py-1 w-full"
          >
            {secteurOptions.map((s) => (
              <option key={s} value={s}>
                {secteurLabel[s]}
              </option>
            ))}
          </select>
        </td>

        {/* 🔎 Recherche client */}
        <td className="p-3 relative">
          <input
            type="text"
            value={p.nom ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              handleChange(i, "nom", value);
              searchClient(i, value); // 🔥 ta fonction debounce
            }}
            placeholder="Scanner QR ou rechercher client"
            className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1"
          />

          {clientResults[i]?.length > 0 && (
            <div className="absolute z-50 bg-slate-800 border border-slate-700 rounded w-full mt-1 max-h-40 overflow-y-auto shadow-lg">
              {clientResults[i].map((client: any) => {
                const display =
                  client.raisonSociale ||
                  client.nomEntreprise ||
                  `${client.prenom ?? ""} ${client.nom ?? ""}`.trim();

                return (
                  <div
                    key={client.id}
                    onClick={() => {
                      const updated = [...points];

                      updated[i] = {
                        ...updated[i],
                        clientId: client.id,
                        nom: display,
                        qr: String(client.id), // ou client.referenceClient si dispo
                        secteur:
                          client.secteur || updated[i].secteur,
                      };

                      setPoints(updated);
                      setClientResults((prev) => ({
                        ...prev,
                        [i]: [],
                      }));
                    }}
                    className="px-3 py-2 hover:bg-blue-600 cursor-pointer text-sm"
                  >
                    {display}
                  </div>
                );
              })}
            </div>
          )}

          {errors[i] && (
            <p className="text-red-500 text-xs mt-1">
              {errors[i]}
            </p>
          )}
        </td>

        {/* Action */}
        <td className="p-3 text-right">
          <button
            onClick={() => handleDelete(i)}
            className="text-red-400 hover:text-red-300 text-sm transition"
          >
            🗑 Supprimer
          </button>
        </td>
      </tr>
    ))
  )}
</tbody>
          </table>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            onClick={handleAdd}
            className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg shadow-md transition"
          >
            ➕ Ajouter un point
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-5 py-2 rounded-lg shadow-md transition ${
              saving ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            } text-white`}
          >
            {saving ? "💾 Enregistrement..." : "💾 Enregistrer (PUT)"}
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-xs pt-4">
          © {new Date().getFullYear()} EspoirAfriqueSas — Tous droits réservés.
          <br />
          <span className="text-slate-400">Développé avec ❤️ par Tengs-Labs Platinum PRO</span>
        </div>
      </motion.div>
    </div>
  );
};
 
export default PointsEdit;